import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Zap,
  ArrowRight,
  ExternalLink,
  Clock,
  Copy,
  Share2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ACCENTS = {
  defi_deposit: "text-green-400 border-green-500/30 bg-green-500/10",
  yield: "text-green-400 border-green-500/30 bg-green-500/10",
  withdrawal_completed: "text-green-400 border-green-500/30 bg-green-500/10",
  withdrawal_requested: "text-orange-400 border-orange-500/30 bg-orange-500/10",
  swap_deposit: "text-orange-400 border-orange-500/30 bg-orange-500/10",
  swap_sell_deposit: "text-orange-400 border-orange-500/30 bg-orange-500/10",
  decision: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  transaction: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  alert: "text-red-400 border-red-500/30 bg-red-500/10",
  agent_closed: "text-muted-foreground border-border bg-muted/40",
  bot_trade: "text-green-400 border-green-500/30 bg-green-500/10",
};

function prettyType(t) {
  if (!t) return "Activity";
  return t
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * ActivityShare — publicly accessible (no wallet required) single-activity view.
 * URL: /activity/:activityId — paired with the OG-tagged backend route at
 * /api/share/activity/:activityId which is what X/Discord/Telegram crawl.
 */
export default function ActivityShare() {
  const { activityId } = useParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/activities/${activityId}`);
        if (!res.ok) {
          if (active) setActivity(null);
          return;
        }
        const data = await res.json();
        if (active) setActivity(data || null);
      } catch {
        if (active) setActivity(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [activityId]);

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/activity/${activityId}`
      : `/activity/${activityId}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it on X");
    } catch {
      toast.error("Copy failed — long-press the URL bar to copy");
    }
  }

  function shareToX() {
    const text = activity?.title || activity?.description || "Live on STXOSHI";
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(
      text
    )}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (loading) {
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center text-muted-foreground"
        data-testid="activity-share-loading"
      >
        Loading activity…
      </div>
    );
  }
  if (!activity) {
    return (
      <div
        className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 px-6 text-center"
        data-testid="activity-share-not-found"
      >
        <h1 className="text-xl font-heading">Activity not found</h1>
        <p className="text-sm text-muted-foreground">
          This event may have been archived.
        </p>
        <Link to="/" className="text-secondary hover:underline">
          Back to STXOSHI →
        </Link>
      </div>
    );
  }

  const accent = TYPE_ACCENTS[activity.type] || TYPE_ACCENTS.transaction;
  const ts = activity.timestamp ? new Date(activity.timestamp) : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Public header — no wallet connect (unauthenticated route). */}
      <header className="border-b border-border/40 px-4 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="font-heading font-bold tracking-wider text-orange-400"
          data-testid="share-brand"
        >
          STXOSHI
        </Link>
        <Link to="/" data-testid="share-cta">
          <Button size="sm" variant="outline">
            Explore STXOSHI
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </header>

      <main className="p-4 md:p-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Hero card */}
          <Card
            className="glass-card border-border"
            data-testid="activity-share-hero"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <Zap className="w-5 h-5 text-secondary" />
                <Badge
                  variant="outline"
                  className={`text-xs uppercase tracking-wider ${accent}`}
                  data-testid="activity-type-pill"
                >
                  {prettyType(activity.type)}
                </Badge>
                {activity.protocol && (
                  <Badge variant="outline" className="text-xs">
                    {activity.protocol}
                  </Badge>
                )}
                {ts && (
                  <span
                    className="text-xs text-muted-foreground font-mono flex items-center gap-1"
                    data-testid="activity-timestamp"
                  >
                    <Clock className="w-3 h-3" />
                    {ts.toLocaleString()}
                  </span>
                )}
              </div>

              <h1
                className="font-heading text-2xl md:text-3xl font-bold mb-2"
                data-testid="activity-title"
              >
                {activity.title || activity.description || "STXOSHI activity"}
              </h1>

              {activity.description && activity.description !== activity.title && (
                <p
                  className="text-sm text-muted-foreground leading-relaxed"
                  data-testid="activity-description"
                >
                  {activity.description}
                </p>
              )}

              {/* Amount / P&L headline */}
              {(activity.amount || activity.pnl_impact) ? (
                <div className="mt-6 pt-6 border-t border-border">
                  {activity.amount ? (
                    <>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        Amount
                      </p>
                      <p
                        className="font-mono text-3xl font-bold text-orange-400"
                        data-testid="activity-amount"
                      >
                        {Number(activity.amount).toLocaleString(undefined, {
                          maximumFractionDigits: 4,
                        })}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        P&L impact
                      </p>
                      <p
                        className={`font-mono text-3xl font-bold ${
                          activity.pnl_impact >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                        data-testid="activity-pnl"
                      >
                        {activity.pnl_impact >= 0 ? "+" : ""}
                        {Number(activity.pnl_impact).toLocaleString(undefined, {
                          maximumFractionDigits: 4,
                        })}
                      </p>
                    </>
                  )}
                </div>
              ) : null}

              {/* Share actions */}
              <div className="mt-6 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyLink}
                  data-testid="copy-link-btn"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy link
                </Button>
                <Button
                  size="sm"
                  onClick={shareToX}
                  data-testid="share-to-x-btn"
                  className="bg-black text-white hover:bg-neutral-900"
                >
                  <Share2 className="w-3 h-3 mr-1" />
                  Share on X
                </Button>
                <a
                  href={`${API}/api/share/activity/${activityId}/og.png`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-secondary self-center ml-1"
                  data-testid="og-image-link"
                >
                  <ExternalLink className="w-3 h-3" />
                  View social card
                </a>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="border-orange-500/30 bg-orange-500/5">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                Want to run your own AI prediction bot?
              </p>
              <Link to="/" data-testid="activity-bottom-cta">
                <Button>
                  Get started on STXOSHI
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
